/**
 * InfraVelox — Frontend Security Utilities
 * Sanitizes inputs, validates data, and provides XSS guards.
 */

// ─── XSS: Strip dangerous HTML tags/attributes from any string ───────────────
export const sanitizeInput = (str) => {
  if (typeof str !== 'string') return str;
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// ─── Sanitize all string fields in a form object ──────────────────────────────
export const sanitizeForm = (formObj) => {
  const sanitized = {};
  for (const [key, val] of Object.entries(formObj)) {
    sanitized[key] = typeof val === 'string' ? val.trim() : val;
  }
  return sanitized;
};

// ─── Email validation ─────────────────────────────────────────────────────────
export const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// ─── Password strength check ──────────────────────────────────────────────────
export const passwordStrength = (pwd) => {
  if (!pwd || pwd.length < 8) return { score: 0, label: 'Too short', color: '#ef4444' };
  let score = 0;
  if (pwd.length >= 8)  score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  const levels = [
    { score: 1, label: 'Very Weak',  color: '#ef4444' },
    { score: 2, label: 'Weak',       color: '#f97316' },
    { score: 3, label: 'Fair',       color: '#eab308' },
    { score: 4, label: 'Strong',     color: '#22c55e' },
    { score: 5, label: 'Very Strong',color: '#10b981' },
  ];
  return levels.find(l => l.score === Math.min(score, 5)) || levels[0];
};

// ─── Token validation (basic client-side check) ───────────────────────────────
export const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true; // treat malformed tokens as expired
  }
};

// ─── Safe localStorage getters (prevent JSON.parse crashes) ──────────────────
export const safeGetUser = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    const token = localStorage.getItem('token');
    if (token && isTokenExpired(token)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return null;
    }
    return user;
  } catch {
    return null;
  }
};

// ─── Prevent clickjacking at runtime (belt & suspenders with Helmet) ─────────
export const enforceFrameGuard = () => {
  if (window.self !== window.top) {
    // If page is embedded in an iframe, redirect out
    window.top.location = window.self.location;
  }
};

// ─── Rate limit local actions (e.g. prevent spam form submit clicks) ─────────
const _actionTimestamps = {};
export const isRateLimited = (key, limitMs = 2000) => {
  const now = Date.now();
  if (_actionTimestamps[key] && now - _actionTimestamps[key] < limitMs) return true;
  _actionTimestamps[key] = now;
  return false;
};
